import { useId, useRef, useState, type ReactNode } from 'react';
import Dialog from '../ui/Dialog';
import Menu, { type MenuItemDef } from '../ui/Menu';
import Popover from '../ui/Popover';
import { useIsDesktop, useMediaQuery } from '../ui/useOverlay';

/**
 * A menu button for lists (PRD §4.7, D5): a `ui/Popover` + `ui/Menu` on
 * pointer devices, a bottom sheet with touch-sized rows on phones and coarse
 * pointers. Used by the row "⋯", the inline status picker, the view switcher
 * and the page "⋯".
 */
export interface MenuButtonProps {
  items: MenuItemDef[];
  /** Accessible name of the trigger (and of the menu). */
  label: string;
  /** Visible content of the trigger. */
  children: ReactNode;
  triggerClassName?: string;
  /** Which inline edge of the trigger the popover lines up with. */
  align?: 'start' | 'end';
  /** Title of the sheet on touch (default: `label`). */
  sheetTitle?: ReactNode;
  /** `auto`: sheet below md or on a coarse pointer. */
  mode?: 'auto' | 'popover' | 'sheet';
  /** Popover width / padding classes. */
  popoverClassName?: string;
  /** Show the label as a native tooltip (icon-only triggers). */
  showTooltip?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hooks for tests / CSS on the trigger. */
  triggerProps?: Record<`data-${string}`, string>;
  /** Extra ARIA/role attributes on the trigger (e.g. role=tab when it sits inside a tablist). */
  triggerAria?: { role?: string; 'aria-selected'?: boolean; tabIndex?: number };
}

export function useTouchMenus(): boolean {
  const isDesktop = useIsDesktop();
  const coarse = useMediaQuery('(pointer: coarse)');
  return !isDesktop || coarse;
}

export default function MenuButton({
  items,
  label,
  children,
  triggerClassName = '',
  align = 'end',
  sheetTitle,
  mode = 'auto',
  popoverClassName = 'w-60 p-1.5',
  showTooltip = false,
  onOpenChange,
  triggerProps,
  triggerAria,
}: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const touch = useTouchMenus();
  const asSheet = mode === 'sheet' || (mode === 'auto' && touch);

  const change = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  const dismiss = (refocus: boolean) => {
    change(false);
    if (refocus && !asSheet) triggerRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        title={showTooltip ? label : undefined}
        onClick={(e) => { e.stopPropagation(); change(!open); }}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); change(true); } }}
        className={triggerClassName}
        {...triggerAria}
        {...triggerProps}
      >
        {children}
      </button>
      {asSheet ? (
        <Dialog
          open={open}
          onClose={() => dismiss(false)}
          title={sheetTitle ?? label}
          variant="sheet"
          detent="medium"
          bodyClassName="px-3 pb-4"
        >
          <Menu id={menuId} items={items} label={label} large closeOnTab={false} onDismiss={() => dismiss(false)} />
        </Dialog>
      ) : (
        <Popover
          open={open}
          onClose={(reason) => dismiss(reason !== 'outside')}
          anchorRef={triggerRef}
          align={align}
          className={popoverClassName}
        >
          <Menu id={menuId} items={items} label={label} onDismiss={() => dismiss(true)} />
        </Popover>
      )}
    </>
  );
}
